import { apiGetCustomers } from "@lib/api/customer";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { CustomerName } from "@lib/components/MiniComponents/Others";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { walletTypeColor } from "@lib/helpers/ColorHelper";
import { createYupSync } from "@lib/helpers/TableHelper";
import { useTypedSelector } from "@lib/redux";
import { walletSchema as yupSchema } from "@lib/schemas/wallet";
import { AuthRoles } from "@lib/types";
import {
  PaginationType,
  UseMutationProps,
  UseQueryProps,
} from "@lib/types/Api";
import { SendByteWallet, SendByteWalletType } from "@lib/types/SendByte";
import { SendByteWalletUsage } from "@lib/types/Wallet";
import { useQuery } from "@tanstack/react-query";
import { Form, Input, Modal, Select, Space, Tag } from "antd";
import { useCallback, useEffect, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import useIntl from "react-intl/lib/src/components/useIntl";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props = {
  openModal: boolean;
  toggleModal: (status?: boolean) => void;
  mutationQuery: UseMutationProps<SendByteWallet>;
  wallet?: SendByteWallet;
  walletQuery: UseQueryProps<PaginationType<SendByteWallet>>;
  customerId?: string;
};

export default function SendbyteWalletModal({
  openModal,
  toggleModal,
  mutationQuery,
  wallet,
  walletQuery,
  customerId,
}: Props) {
  const { messages } = useIntl();
  const [form] = Form.useForm();
  const { user } = useTypedSelector((state) => state.auth);
  const userRole = user?.role;

  const { mutateAsync, isPending } = mutationQuery;

  const { data: customers, isLoading: customersIsLoading } = useQuery({
    queryFn: () =>
      apiGetCustomers({
        limit: 100,
      }),
    queryKey: ["customers-dropdown"],
    enabled: openModal && userRole === AuthRoles.Admin,
  });
  const customersApiData = customers?.data?.data;

  const initialValues = useMemo(() => {
    return (
      wallet ??
      ({
        name: "",
        usage: SendByteWalletUsage.Lead,
        customerId: customerId || user?.customerId,
      } as SendByteWallet)
    );
  }, [wallet, user?.customerId]);

  useEffect(() => {
    if (typeof form?.setFieldsValue !== "undefined") {
      form?.setFieldsValue(initialValues);
    }
  }, [form, openModal, initialValues]);

  const handleClose = useCallback(() => {
    toggleModal(false);
    form?.resetFields();
  }, [toggleModal, form]);

  return (
    <Modal
      title={
        <FormattedMessage
          id={wallet ? "common.editTitle" : "common.createTitle"}
          values={{ title: messages["common.wallet"] as string }}
        />
      }
      open={openModal}
      onCancel={handleClose}
      footer={null}
      confirmLoading={isPending}
    >
      {openModal && (
        <Form<SendByteWallet>
          {...layout}
          form={form}
          name={!!wallet?.id ? `wallet-update-${wallet.id}` : "wallet-add"}
          onFinish={async (values) => {
            await mutateAsync({
              ...values,
              usage: values.usage || SendByteWalletUsage.Lead,
              id: wallet?.id,
            });
            await walletQuery.refetch();
          }}
          style={{ maxWidth: 600, marginTop: 20 }}
          autoComplete="off"
          initialValues={wallet}
        >
          <Form.Item
            name="name"
            label={messages["common.walletName"] as string}
            rules={[createYupSync({ schema: yupSchema })]}
          >
            <Input
              value={form?.getFieldValue("name")}
              onChange={({ target }) => {
                form?.setFieldValue(
                  "name",
                  (target as any).value.toUpperCase(),
                );
              }}
            />
          </Form.Item>

          <Form.Item
            name="type"
            label={messages["common.walletType"] as string}
            rules={[createYupSync({ schema: yupSchema })]}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="text"
              options={Object.values(SendByteWalletType).map((type) => ({
                label: (
                  <Tag color={walletTypeColor(type)}>
                    {messages["walletType"][type] as string}
                  </Tag>
                ),
                text: messages["walletType"][type] as string,
                value: type,
              }))}
            />
          </Form.Item>

          {userRole === AuthRoles.Admin && (
            <Form.Item
              name="customerId"
              label={messages["common.customer"] as string}
              rules={[createYupSync({ schema: yupSchema })]}
            >
              <Select
                showSearch
                loading={customersIsLoading}
                optionFilterProp="text"
                options={customersApiData?.map((customer) => ({
                  label: <CustomerName customer={customer} />,
                  text: customer.name,
                  value: customer.id,
                }))}
              />
            </Form.Item>
          )}

          <StyledFlex style={{ justifyContent: "end" }}>
            <Space>
              {!wallet && (
                <ActionButton
                  type="reset"
                  onClick={handleClose}
                  loading={isPending}
                />
              )}

              <ActionButton
                type={!!wallet ? "save" : "add"}
                loading={isPending}
              />
            </Space>
          </StyledFlex>
        </Form>
      )}
    </Modal>
  );
}
